import { Paper, Avatar, Box, Typography, Checkbox, Chip, Button, Tooltip } from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { validationReasonText, formatValidationPhone } from './AtsValidateDialog';
import { documentsProgress, PROGRESS_TONES } from './AtsBulkRequestDocumentsDialog';

export const CandidateCard = ({
  application,
  isDragging = false,
  onClick,
  selectable = false,
  isSelected = false,
  onSelect,
  stageType = null,
  onStageAction,
  canAct = false,
  cvScore = null,
  // Columna "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): motivo,
  // teléfono para contactar a la persona y botón "Validar"
  onValidate = null,
}) => {
  const { t } = useTranslation();
  // La revisión de antecedentes solo se muestra en organizaciones de Chile
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  const name = application.candidateName || application.candidateEmail || application.candidateId;
  const initials = (name || 'C').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const stageActionLabel = stageType === 'offer'
    ? t('ats.offer.cardAction')
    : stageType === 'hired'
      ? t('ats.hire.cardAction')
      : null;
  const showStageAction = !!(canAct && stageActionLabel && onStageAction);
  const pendingValidation = application.status === 'pending_validation';
  // Avance de los documentos pedidos (PLAN_SOLICITUD_DOCUMENTOS_MASIVA)
  const docs = documentsProgress(application);

  return (
    <Paper
      elevation={isDragging ? 6 : 0}
      onClick={onClick}
      sx={{
        p: 1.5,
        border: isSelected ? '1px solid #6A5AF9' : '1px solid #E5E7EB',
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        backgroundColor: isDragging || isSelected ? '#F5F3FF' : '#fff',
        cursor: isDragging ? 'grabbing' : 'pointer',
        opacity: isDragging ? 0.9 : 1,
        userSelect: 'none',
        boxShadow: isSelected ? '0 2px 8px rgba(106,90,249,0.15)' : 'none',
        '&:hover': { borderColor: '#6A5AF9', boxShadow: '0 2px 8px rgba(106,90,249,0.15)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {selectable && (
          <Checkbox
            checked={isSelected}
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (onSelect) onSelect(!isSelected);
            }}
            sx={{
              p: 0,
              color: '#9CA3AF',
              '&.Mui-checked': { color: '#6A5AF9' },
            }}
          />
        )}

        <Avatar sx={{
          width: 28, height: 28, fontSize: '0.7rem',
          backgroundColor: '#EDE9FE', color: '#6A5AF9', fontWeight: 700, flexShrink: 0,
        }}>
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" fontWeight={600}
            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {application.candidateName || '—'}
          </Typography>
          {application.candidateEmail && (
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.65rem' }}>
              {application.candidateEmail}
            </Typography>
          )}
          {application.offerStatus && (
            <Chip
              label={t(application.offerStatus === 'accepted' ? 'ats.offer.accepted' : 'ats.offer.rejected')}
              size="small"
              color={application.offerStatus === 'accepted' ? 'success' : 'error'}
              sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }}
            />
          )}
          {/* Las marcas ⚠ (captationMarks) se ven solo en el drawer (Erwin, 2026-10-07) */}
          {!orgCountryLoading && operatesInChile &&
            application.backgroundReview?.status === 'requiere_revision' &&
            application.backgroundReview?.decision?.status !== 'approved' && (
            <Tooltip title={(application.backgroundReview.reasons || []).join(' · ')} arrow>
              <Chip
                label={t('ats.backgroundReview.cardChip')}
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', mt: 0.5, mr: 0.5, backgroundColor: '#FFEDD5', color: '#C2410C', fontWeight: 700 }}
              />
            </Tooltip>
          )}
          {application.autoDiscarded && (
            <Tooltip title={application.discardReason || ''} arrow>
              <Chip
                label={t('ats.captationRules.autoDiscardChip')}
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', mt: 0.5, backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }}
              />
            </Tooltip>
          )}
          {docs.total > 0 && (
            <Tooltip
              arrow
              title={(application.documentRequest.items || [])
                .map((it) => `${it.label}: ${it.status === 'uploaded' ? t('ats.documents.uploaded') : t('ats.documents.pending')}`)
                .join(' · ')}
            >
              <Chip
                label={docs.state === 'complete'
                  ? t('ats.documents.cardChipComplete')
                  : t('ats.documents.cardChip', { uploaded: docs.uploaded, total: docs.total })}
                size="small"
                sx={{
                  height: 16, fontSize: '0.6rem', mt: 0.5, mr: 0.5, fontWeight: 700,
                  backgroundColor: PROGRESS_TONES[docs.state].bg, color: PROGRESS_TONES[docs.state].color,
                }}
              />
            </Tooltip>
          )}
        </Box>

        {typeof cvScore === 'number' && (
          <Tooltip title={t('ats.pipeline.cvScoreTooltip')}>
            <Chip
              label={`${cvScore}%`}
              size="small"
              sx={{
                ml: 'auto', flexShrink: 0, height: 16, fontSize: '0.6rem', fontWeight: 700,
                backgroundColor: cvScore >= 70 ? '#DCFCE7' : cvScore >= 50 ? '#FEF9C3' : '#FEE2E2',
                color: cvScore >= 70 ? '#16A34A' : cvScore >= 50 ? '#CA8A04' : '#DC2626',
              }}
            />
          </Tooltip>
        )}
      </Box>

      {pendingValidation && (
        <Box data-testid="pending-validation-info">
          <Typography variant="caption" sx={{ display: 'block', color: '#92400E', lineHeight: 1.35 }}>
            {validationReasonText(t, application.validation)}
          </Typography>
          {application.validation?.contactPhone && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('ats.validation.phone', { phone: formatValidationPhone(application.validation.contactPhone) })}
            </Typography>
          )}
          {onValidate && (
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onValidate(application); }}
              sx={{
                mt: 0.75, fontSize: '0.7rem', py: 0.25,
                borderColor: '#D97706', color: '#B45309',
                '&:hover': { borderColor: '#B45309', backgroundColor: '#FFFBEB' },
              }}
            >
              {t('ats.validation.validate')}
            </Button>
          )}
        </Box>
      )}

      {showStageAction && (
        <Button
          size="small"
          variant="outlined"
          fullWidth
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onStageAction(application, stageType); }}
          sx={{
            fontSize: '0.7rem', py: 0.25,
            borderColor: '#6A5AF9', color: '#6A5AF9',
            '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' },
          }}
        >
          {stageActionLabel}
        </Button>
      )}
    </Paper>
  );
};

export const SortableCandidateCard = ({
  application,
  onCardClick,
  selectable = false,
  isSelected = false,
  onSelectionChange,
  stageType = null,
  onStageAction,
  canAct = false,
  cvScore = null,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: application.uid || application.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CandidateCard
        application={application}
        selectable={selectable}
        isSelected={isSelected}
        stageType={stageType}
        onStageAction={onStageAction}
        canAct={canAct}
        cvScore={cvScore}
        onSelect={(checked) => {
          if (onSelectionChange) onSelectionChange(application, checked);
        }}
        onClick={(e) => {
          if (!isDragging) { e.stopPropagation(); onCardClick(application); }
        }}
      />
    </div>
  );
};